import { db, functions, auth, storage } from '@/shared/services/firebase'
import axios from '@hw-core/libs/axios'
import store from '@/shared/store'
import moment from 'moment'
import { fetchApplications } from './actions/fetchApplications'

export default {
	namespaced: true,
	state: {},
	getters: {},
	mutations: {},
	actions: {
		fetchApplications,
		fetchInterview(ctx, { assignedTo, callback }) {
			db.collection('interviews')
				.where('assignedTo', '==', assignedTo)
				.onSnapshot({ includeMetadataChanges: true }, async snapshot => {
					const interviews = await Promise.all(
						snapshot.docs.map(async snap => {
							const item = snap.data()
							item.id = snap.id
							item.steps = await Promise.all(
								item.steps.map(async step => {
									if (step.questionType === 'audio' && step.answer) {
										// Get audio filename
										const audioFileName = step.answer.match(/record_audio_(\d+).wav/)[0]
										// Folder name
										const folder = `interviews/${item.id}`
										const ref = storage.ref(`${folder}/${audioFileName}`)
										const url = await ref.getDownloadURL()
										return {
											...step,
											answer: url,
										}
									}
									return step
								})
							)
							return item
						})
					)
					callback(interviews[0])
				})
		},
		fetchApplication(ctx, { id, callback }) {
			db.collection('users')
				.doc(id)
				.onSnapshot(
					{
						includeMetadataChanges: true,
					},
					doc => {
						const data = doc.data()
						data.id = id
						callback(data)
					}
				)
		},
		async addApplication(ctx, userData) {
			try {
				const user = userData
				const dateFormat = 'DD.MM.YYYY'
				user.birthday = moment(user.birthday, dateFormat).format('YYYY-MM-DD')
				user.created = new Date()
				await db.collection('users').add(user)
				return
			} catch (error) {
				throw new Error('Error creating user', error)
			}
		},
		updateUser(ctx, { id, newState }) {
			return new Promise((resolve, reject) => {
				db.collection('users')
					.doc(id)
					.update({ ...newState })
					.then(async () => {
						if (newState.confirmed) {
							await axios.post(`${store.state.app.appConfig.type === 'hw-admin' ? functions.url : functions.oiwaUrl}/sendWelcomeEmail`, { id })
						}
						resolve()
					})
					.catch(() => {
						reject()
					})
			})
		},
		deleteApplication(ctx, { id }) {
			return new Promise((resolve, reject) => {
				db.collection('users')
					.doc(id)
					.delete()
					.then(() => {
						resolve()
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		createInterview(ctx, { form }) {
			return new Promise((resolve, reject) => {
				db.collection('interviews')
					.add({ ...form })
					.then(response => {
						resolve(response.id)
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		updateInterview(ctx, { id, newState }) {
			return new Promise((resolve, reject) => {
				db.collection('interviews')
					.doc(id)
					.update({ ...newState })
					.then(async () => {
						resolve()
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		createContract(ctx, { user }) {
			return new Promise((resolve, reject) => {
				auth.currentUser
					.getIdToken(true)
					.then(idToken => {
						axios
							.post(`${functions.url}/createContractDocument`, { token: idToken, user })
							.then(response => resolve(response.data))
							.catch(error => reject(error))
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		requestContractSignature(ctx, { contractInfo }) {
			return new Promise((resolve, reject) => {
				auth.currentUser
					.getIdToken(true)
					.then(idToken => {
						axios
							.post(`${functions.url}/requestContractSignature`, { token: idToken, contractInfo })
							.then(response => resolve(response))
							.catch(error => reject(error))
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		getContractStatus(ctx, { userId }) {
			return new Promise((resolve, reject) => {
				auth.currentUser
					.getIdToken(true)
					.then(idToken => {
						axios
							.post(`${functions.url}/getContractStatus`, { token: idToken, userId })
							.then(response => resolve(response.data))
							.catch(error => reject(error))
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		sendInterviewEmail(ctx, { to, message, subject }) {
			return new Promise((resolve, reject) => {
				axios
					.post(`${functions.url}/sendEmail`, { to, message, subject })
					.then(response => resolve(response))
					.catch(error => reject(error))
			})
		},
		getInterviewTemplates() {
			return new Promise((resolve, reject) => {
				db.collection('interviews')
					.where('isTemplate', '==', true)
					.get()
					.then(snapshot => {
						const templates = snapshot.docs.map(snap => {
							const item = snap.data()
							item.id = snap.id
							return { label: item.templateName, value: item }
						})
						resolve(templates)
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		createZendeskTicket(ctx, { requester, groupId, message, subject, collaborators }) {
			return new Promise((resolve, reject) => {
				auth.currentUser
					.getIdToken(true)
					.then(idToken => {
						axios
							.post(`${functions.url}/createZendeskTicket`, {
								requester,
								groupId,
								message,
								subject,
								collaborators,
								token: idToken,
							})
							.then(response => resolve(response))
							.catch(error => reject(error))
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		sendEmail(ctx, { to, message, subject }) {
			return new Promise((resolve, reject) => {
				axios
					.post(`${functions.url}/sendEmail`, { to, message, subject })
					.then(response => resolve(response))
					.catch(error => reject(error))
			})
		},
		sendSMS(ctx, { mobile, message }) {
			return new Promise((resolve, reject) => {
				auth.currentUser
					.getIdToken(true)
					.then(idToken => {
						axios
							.post(`${functions.url}/sendSMSMessage`, { to: mobile, message, token: idToken })
							.then(response => resolve(response))
							.catch(error => reject(error))
					})
					.catch(error => {
						reject(error)
					})
			})
		},
		fetchStatistics(ctx, { callback }) {
			db.collection('dashboard')
				.doc('applications')
				.onSnapshot(
					{
						includeMetadataChanges: true,
					},
					doc => {
						callback(doc.data())
					}
				)
		},
		createClientRequest(ctx, clientRequest) {
			return new Promise((resolve, reject) => {
				db.collection('clientRequests')
					.add({
						...clientRequest,
						createdAt: new Date(),
					})
					.then(response => {
						resolve(response.id)
					})
					.catch(err => {
						reject(err)
					})
			})
		},
		closeClientRequest(ctx, userId) {
			return new Promise((resolve, reject) => {
				db.collection('clientRequests')
					.where('assignedTo', '==', userId)
					.where('type', '==', 'application_request')
					.get()
					.then(async snapshot => {
						const data = snapshot.docs.map(doc => {
							const item = doc.data()
							item.id = doc.id
							return item
						})
						const request = data[0]
						await db
							.collection('clientRequests')
							.doc(request.id)
							.update({
								status: 'closed',
								updates: 0,
							})
						resolve()
					})
					.catch(err => {
						reject(err)
					})
			})
		},
		fetchApplicationRequests() {
			return new Promise((resolve, reject) => {
				db.collection('clientRequests')
					.where('type', '==', 'application_request')
					.where('status', '==', 'open')
					.get()
					.then(snapshot => {
						let count = 0
						snapshot.docs.forEach(doc => {
							const item = doc.data()
							if (item.updates) {
								count += item.updates
							}
						})
						resolve(count)
					})
					.catch(() => reject())
			})
		},
		async deleteInterviewTemplate(ctx, { template }) {
			try {
				await db.collection('interviews').doc(template.id).delete()
				return true
			} catch (error) {
				return Promise.reject(error)
			}
		},
	},
}
